import { fromDate, getLocalTimeZone } from "@internationalized/date";
import { visibilitySchemaResponse } from "shared/form/visibilitySchema";
import { countryCode as countryCodeSchema } from "shared/types/countryCode";
import { z } from "zod";

const textSchema = z.object({
  title: z.string(),
  description: z.string(),
});

type TextSchema = z.infer<typeof textSchema>;

const requireTitleAndDescription = (val: TextSchema, ctx: z.RefinementCtx) => {
  const titleLength = val.title.length;
  const descriptionLength = val.description.length;
  if (titleLength === 0) {
    ctx.addIssue({
      code: "custom",
      path: ["title"],
      message: "Du må skrive en tittel for å kunne opprette hendelsen",
    });
  }
  if (descriptionLength === 0) {
    ctx.addIssue({
      code: "custom",
      path: ["description"],
      message: "Du må skrive en melding for å kunne opprette hendelsen",
    });
  }
};

const optionalTitleAndDescription = (
  val: TextSchema | undefined,
  ctx: z.RefinementCtx,
) => {
  if (!val) return;
  const titleLength = val.title.length;
  const descriptionLength = val.description.length;
  if (titleLength === 0 && descriptionLength === 0) return;
  requireTitleAndDescription(val, ctx); // Validate title and description as it is not empty
};

/**
 * requireTitleAndDescription: You cannot leave title and description empty once your language is defined
 * optionalTitleAndDescription: You can leave title and description empty even if your language is defined
 */
const textMapSchema = z.object({
  NOB: textSchema.superRefine(requireTitleAndDescription).optional(),
  SWE: textSchema.superRefine(requireTitleAndDescription).optional(),
  ENG: textSchema.superRefine(optionalTitleAndDescription).optional(),
});

export type TextMap = z.infer<typeof textMapSchema>;

export const customTextSchema = z.object({
  contexts: z.array(z.string()),
  texts: textMapSchema,
});

export type CustomTexts = z.infer<typeof customTextSchema>;

export const globalEventSchema = z.object({
  type: z.string(),
  uuid: z.string(),
  updatedAt: z.coerce
    .date()
    .transform((time) => fromDate(time, getLocalTimeZone())),
  visibility: visibilitySchemaResponse,
  referencedIncidentIds: z.array(z.string()),
  customTexts: customTextSchema.array(),
  isVisible: z.boolean(),
  countryCode: countryCodeSchema,
});

export type GlobalEventResponse = z.infer<typeof globalEventSchema>;

export const globalEventsResponseSchema = z.array(globalEventSchema);
